import styled from 'styled-components';

import { Button } from '@components/button';
import { CopyText } from '@components/copy-text-field';
import { IconBuilder } from '@components/icons/builder';
import { hideSidebar, Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { MCPServer } from 'api/mcp/requests';
interface ThirdPartyConnectionSidebarProps {
  server: MCPServer;
}

export const useThirdPartyConnectionSidebar = () => {
  return useSidebar(ThirdPartyConnectionSidebar, {
    variant: 'CONTENT_HEAVY',
  });
};

const ThirdPartyConnectionSidebar = ({ server }: ThirdPartyConnectionSidebarProps) => {
  const { t } = useScopedTranslate(locale);

  const connectorUrl = server.serverUrl;
  const authorizationUrl = `${server.serverUrl}/oauth/authorize`;
  const tokenUrl = `${server.serverUrl}/oauth/token`;
  const refreshTokenUrl = `${server.serverUrl}/oauth/refresh`;

  return (
    <Sidebar>
      <Sidebar.SimpleHeader title={t('connectWithThirdPartyApps')} icon={IconBuilder} />
      <Sidebar.Content>
        <Stack space={6}>
          <Section>
            <LeftColumn>
              <Text font="semibold14" text={t('publicUrl')} />
            </LeftColumn>
            <Stack space={4}>
              <Stack space={1}>
                <Text font="regular14" text={t('publicUrlDescription')} color="textSecondary" />
                <Text font="semibold14" text={t('publicUrlHighlight')} color="textSecondary" />
              </Stack>
              <CopyText label={t('connectorUrl')} value={connectorUrl} />
            </Stack>
          </Section>

          <Sidebar.Separator />
          <Section>
            <LeftColumn>
              <Text font="semibold14" text={t('oauth20Details')} />
            </LeftColumn>
            <Stack space={4}>
              <Stack space={1}>
                <Text font="regular14" text={t('oauth20Description')} color="textSecondary" />
                <Text font="semibold14" text={t('oauth20Highlight')} color="textSecondary" />
              </Stack>
              <CopyText
                label={t('clientId')}
                helpText={t('clientIdDescription')}
                value={server.clientId}
              />
              <CopyText
                label={t('clientSecret')}
                helpText={t('clientSecretDescription')}
                value={server.clientSecret}
              />
              <CopyText
                label={t('authorizationUrl')}
                helpText={t('authorizationUrlDescription')}
                value={authorizationUrl}
              />
              <CopyText
                label={t('tokenUrl')}
                helpText={t('tokenUrlDescription')}
                value={tokenUrl}
              />
              <CopyText
                label={t('refreshTokenUrl')}
                helpText={t('refreshTokenUrlDescription')}
                value={refreshTokenUrl}
              />
            </Stack>
          </Section>
        </Stack>
      </Sidebar.Content>
      <Sidebar.Footer>
        <Stack orientation="horizontal" justify="flex-end" space={4}>
          <Button variant="secondary" text={t('close')} onClick={hideSidebar} />
        </Stack>
      </Sidebar.Footer>
    </Sidebar>
  );
};

const Section = styled.div(() => ({
  display: 'grid',
  gridTemplateColumns: `${pxToRem(200)} minmax(0, 1fr)`,
  columnGap: pxToRem(20),
  rowGap: 0,
  alignItems: 'start',
}));

const LeftColumn = styled.div(() => ({
  paddingTop: theme.space[1],
}));

const locale = {
  en: {
    connectWithThirdPartyApps: 'Connect with third-party apps',
    publicUrl: 'Claude',
    publicUrlDescription:
      'Add this URL as a custom connector in Claude (Settings → Connectors → Add custom connector). When Claude opens the sign-in page, enter the Client ID and Client secret shown below.',
    publicUrlHighlight: 'Works in Claude on the web and in the desktop app.',
    connectorUrl: 'Connector URL',
    oauth20Details: 'OAuth 2.0 credentials',
    oauth20Description:
      'Claude asks for the Client ID and Client secret on its sign-in page. Microsoft Copilot Studio needs all of these values when you add the MCP server with OAuth 2.0.',
    oauth20Highlight: 'Matches the fields shown in Copilot Studio.',
    clientId: 'Client ID',
    clientIdDescription: 'Enter this when Claude or Copilot Studio asks for the Client ID.',
    clientSecret: 'Client secret',
    clientSecretDescription:
      'Enter this when asked for the Client secret. Keep this value private.',
    authorizationUrl: 'Authorization URL',
    authorizationUrlDescription: 'Use for the “Authorization URL” input in Copilot Studio.',
    tokenUrl: 'Token URL',
    tokenUrlDescription: 'Populate the “Token URL template” field with this address.',
    refreshTokenUrl: 'Refresh Token URL',
    refreshTokenUrlDescription: 'Use for the “Refresh URL” field when tokens need to be renewed.',
    close: 'Close',
  },
};
